import type { DemandPoint, LayerClassificationResult, LayerConfig, ParcelLayerItem, StreetLayerItem } from "./layer";

/**
 * Message posted to the street-parcel worker. `requestId` correlates the responses. The model is
 * bundled into the worker itself, so it is not sent over `postMessage`.
 */
export interface StreetParcelRequest {
  parcels: ParcelLayerItem[];
  streets: StreetLayerItem[];
  /** Demand (address) points; used to report which addresses fall inside each street parcel. */
  demands?: DemandPoint[];
  config?: LayerConfig;
  requestId: number;
}

/** Messages the worker posts back: incremental progress, then a single final result. */
export type StreetParcelResponse =
  | { type: "progress"; completed: number; total: number; requestId: number }
  | { type: "result"; results: LayerClassificationResult[]; requestId: number };

export interface ClassifyInWorkerOptions {
  parcels: ParcelLayerItem[];
  streets: StreetLayerItem[];
  /** Demand (address) points; used to report which addresses fall inside each street parcel. */
  demands?: DemandPoint[];
  config?: LayerConfig;
  /** Called as parcels are processed (at most ~100 times). */
  onProgress?: (completed: number, total: number) => void;
}

let nextRequestId = 1;

/**
 * Promise wrapper around the street-parcel worker: posts a classification request, forwards progress
 * to `onProgress`, and resolves with the results — correlating by `requestId` so concurrent requests
 * on the same worker don't cross wires. The caller owns the worker's lifecycle (creation/termination).
 */
export function classifyParcelLayerInWorker(worker: Worker, options: ClassifyInWorkerOptions): Promise<LayerClassificationResult[]> {
  const requestId = nextRequestId;
  nextRequestId += 1;
  return new Promise<LayerClassificationResult[]>((resolve) => {
    const onMessage = (event: MessageEvent<StreetParcelResponse>): void => {
      const message = event.data;
      if (message.requestId !== requestId) return;
      if (message.type === "progress") {
        options.onProgress?.(message.completed, message.total);
        return;
      }
      worker.removeEventListener("message", onMessage);
      resolve(message.results);
    };
    worker.addEventListener("message", onMessage);
    const request: StreetParcelRequest = {
      parcels: options.parcels,
      streets: options.streets,
      demands: options.demands,
      config: options.config,
      requestId,
    };
    worker.postMessage(request);
  });
}

/**
 * Classify a layer across a pool of workers. The parcels are split into one contiguous shard per
 * worker and classified in parallel; results are concatenated back into the original order, and
 * `onProgress` reports the combined completion across all shards. The street layer is sent to every
 * worker (each parcel needs its own neighbourhood), so streets are replicated, not sharded.
 *
 * The caller owns the workers' lifecycle. Size the pool to the hardware, e.g.
 * `Math.min((navigator.hardwareConcurrency ?? 4) - 1, 8)`.
 */
export function classifyParcelLayerInPool(workers: Worker[], options: ClassifyInWorkerOptions): Promise<LayerClassificationResult[]> {
  if (workers.length === 0) {
    const message = "classifyParcelLayerInPool requires at least one worker";
    throw new Error(message);
  }
  const { parcels, streets, demands, config, onProgress } = options;
  const total = parcels.length;
  const shardSize = Math.ceil(total / workers.length);
  const completedPerWorker = new Array<number>(workers.length).fill(0);

  return Promise.all(
    workers.map((worker, i) => {
      const shard = parcels.slice(i * shardSize, (i + 1) * shardSize);
      if (shard.length === 0) return Promise.resolve<LayerClassificationResult[]>([]);
      return classifyParcelLayerInWorker(worker, {
        parcels: shard,
        streets,
        demands,
        config,
        onProgress: onProgress
          ? (completed) => {
              completedPerWorker[i] = completed;
              onProgress(
                completedPerWorker.reduce((sum, n) => sum + n, 0),
                total
              );
            }
          : undefined,
      });
    })
  ).then((shardResults) => shardResults.flat());
}
